/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  bulkUploadTariffs,
  createBulkTariffsWithoutDuplicateCheck,
  createTariff,
  deleteTariff,
  deleteBulkTariffs,
  getTariffsByDatabaseId,
  getTariffsByGroupId,
  getTariffById,
  updateTariff,
  updateTariffStatus,
  // Ensure these are exported from your service file
  updateAllTariffsStatus,
  updateTariffsStatusFromTo,
  toggleTariffDocVisibility,
  saveTariffResult,
} from './tariffs.service';
import { Time } from '../shared';
import {
  ITariff,
  PaginationParams,
  UpdateAllStatusParams,
} from './tariffs';
import { useInvalidateAllDatabaseKeys } from '../database/database.hooks';
import { STATS_QUERY_KEYS } from '../stats/stats.hooks';
import { auditKeys } from '../audit/audit.hooks';

// Query key constants
const QUERY_KEYS = {
  TARIFF: 'tariff',
  TARIFFS: 'tariffs',
  BY_DATABASE: 'by-database',
  BY_GROUP: 'by-group',
  BY_ID: 'by-id',
  SUPPLIERS: 'suppliers',
  PRODUCTS: 'products',
  BY_PRODUCT: 'by-product',
  BY_SUPPLIER: 'by-supplier',
} as const;

// Query key factories following best practices
const tariffKeys = {
  all: () => [QUERY_KEYS.TARIFF] as const,
  tariffs: () => [...tariffKeys.all(), QUERY_KEYS.TARIFFS] as const,
  tariffsByDatabase: (
    databaseId: string,
    options?: {
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      search?: string;
      tab?:
        | 'All'
        | 'New'
        | 'Not Reviewed'
        | 'Review Queue'
        | 'Quarantined'
        | 'Barred'
        | 'Cleared'
        | string;
    },
  ) =>
    [
      ...tariffKeys.tariffs(),
      QUERY_KEYS.BY_DATABASE,
      databaseId,
      {
        page: options?.page || 1,
        limit: options?.limit || 10,
        sortBy: options?.sortBy || 'hit_count',
        sortOrder: options?.sortOrder || 'desc',
        search: options?.search || '',
        tab: options?.tab || 'All',
      },
    ] as const,

  // Updated tariffsByGroup to support pagination parameters
  tariffsByGroup: (
    groupId: string,
    options?: {
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      search?: string;
      databaseName?: string;
      exclusions?: string[]; // Fixed typo here
    },
  ) =>
    [
      ...tariffKeys.tariffs(),
      QUERY_KEYS.BY_GROUP,
      groupId,
      {
        page: options?.page || 1,
        limit: options?.limit || 10,
        sortBy: options?.sortBy || 'hit_count',
        sortOrder: options?.sortOrder || 'desc',
        search: options?.search || '',
        databaseName: options?.databaseName || '',
        exclusions: options?.exclusions || [], // Fixed typo here
      },
    ] as const,

  tariff: () => [...tariffKeys.all(), QUERY_KEYS.BY_ID] as const,
  tariffById: (id: string) => [...tariffKeys.tariff(), id] as const,
  suppliers: () => [...tariffKeys.all(), QUERY_KEYS.SUPPLIERS] as const,
  suppliersByProduct: (productId: string) =>
    [...tariffKeys.suppliers(), QUERY_KEYS.BY_PRODUCT, productId] as const,
  products: () => [...tariffKeys.all(), QUERY_KEYS.PRODUCTS] as const,
  productsBySupplier: (supplierId: string) =>
    [...tariffKeys.products(), QUERY_KEYS.BY_SUPPLIER, supplierId] as const,
};

// Hook to get tariffs by database ID with optional search filtering
export const useGetTariffsByDatabaseId = (
  databaseId: string,
  searchTerm = '',
  paginationParams: PaginationParams,
) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'tariffs',
      databaseId,
      paginationParams.page,
      paginationParams.limit,
      paginationParams.sortBy,
      paginationParams.sortOrder,
      searchTerm,
      paginationParams.tab,
      // Part of the key: the colour tab changes the result set, so leaving it
      // out would serve one tab's rows to another from cache.
      paginationParams.statusColor,
    ],
    queryFn: () =>
      getTariffsByDatabaseId(databaseId, {
        ...paginationParams,
        search: searchTerm,
      }),
    staleTime: 0,
    gcTime: 30000,
    enabled: Boolean(databaseId),
  });

  return {
    data: data?.data || [],
    pagination: data?.pagination,
    totals: data?.totals,
    isLoading: isLoading,
    error: error,
    refetch: refetch,
  };
};

// Hook to get tariffs by group ID with optional search filtering
export const useGetTariffsByGroupId = (
  groupId: string,
  searchTerm = '',
  paginationParams: PaginationParams,
) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: tariffKeys.tariffsByGroup(groupId, {
      page: paginationParams.page,
      limit: paginationParams.limit,
      sortBy: paginationParams.sortBy,
      sortOrder: paginationParams.sortOrder,
      search: searchTerm,
      databaseName: paginationParams.databaseName,
      exclusions: paginationParams.exclusions, // Fixed typo
    }),
    queryFn: () =>
      getTariffsByGroupId(groupId, {
        ...paginationParams,
        search: searchTerm,
      }),
    staleTime: 0,
    gcTime: 30000,
    enabled: Boolean(groupId),
  });

  return {
    data: data?.data || [],
    pagination: data?.pagination,
    isLoading: isLoading,
    error: error,
    refetch: refetch,
  };
};

// Hook to get tariff by ID
export const useGetTariffById = (
  id?: string,
  shouldRun?: boolean,
): UseQueryResult<ITariff | null> => {
  return useQuery({
    queryKey: tariffKeys.tariffById(id || ''),
    queryFn: () => getTariffById(id),
    staleTime: 20 * Time.MINUTE,
    enabled: Boolean(id) && shouldRun,
  });
};

// Mutation hook to update tariff
export const useUpdateTariff = (databaseId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      tariffData,
    }: {
      id: string;
      tariffData: Partial<any>;
    }) => updateTariff(id, tariffData),
    onSuccess: (data, variables) => {
      if (databaseId) {
        queryClient.invalidateQueries({
          queryKey: ['tariffs', databaseId],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: [STATS_QUERY_KEYS.stats],
          exact: false,
        });
      } else {
        queryClient.invalidateQueries({ queryKey: tariffKeys.all() });
      }

      queryClient.invalidateQueries({
        queryKey: tariffKeys.tariffById(variables.id),
      });

      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogs(),
        exact: false,
      });
    },
  });
};

// Mutation hook to update tariff status
export const useUpdateTariffStatus = (databaseId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      statusData,
    }: {
      id: string;
      statusData: { status_flag: string };
    }) => updateTariffStatus(id, statusData),
    onSuccess: () => {
      if (databaseId) {
        queryClient.invalidateQueries({
          queryKey: ['tariffs', databaseId],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: [STATS_QUERY_KEYS.stats],
          exact: false,
        });
      } else {
        queryClient.invalidateQueries({ queryKey: tariffKeys.all() });
      }

      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogs(),
        exact: false,
      });
    },
  });
};

// NEW: Hook to update ALL tariffs status in a database
export const useUpdateAllTariffsStatus = (databaseId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: UpdateAllStatusParams) =>
      updateAllTariffsStatus(params.databaseId, params.status_flag),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['tariffs', databaseId],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: [STATS_QUERY_KEYS.stats],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogsByDatabase(databaseId),
        exact: false,
      });
    },
  });
};

// NEW: Hook to transition tariffs status (From -> To)
export const useUpdateTariffsStatusFromTo = (databaseId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      databaseId,
      scope,
      toStatus,
      fromStatus,
      entityIds,
    }: {
      databaseId: string;
      scope: string;
      toStatus: string;
      fromStatus?: string;
      entityIds?: string[];
    }) =>
      updateTariffsStatusFromTo(
        databaseId,
        scope,
        toStatus,
        fromStatus,
        entityIds,
      ),
    onSuccess: () => {
      if (databaseId) {
        queryClient.invalidateQueries({
          queryKey: ['tariffs', databaseId],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: [STATS_QUERY_KEYS.stats],
          exact: false,
        });
      } else {
        queryClient.invalidateQueries({ queryKey: tariffKeys.all() });
      }

      // Invalidate database-level audit logs
      if (databaseId) {
        queryClient.invalidateQueries({
          queryKey: auditKeys.auditLogsByDatabase(databaseId),
          exact: false,
        });
      }

      // Broad invalidation to ensure audit logs refresh
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogs(),
        exact: false,
      });
    },
  });
};

// NEW: Hook to toggle tariff doc visibility
export const useToggleTariffDocVisibility = (databaseId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, parentId }: { itemId: string; parentId: string }) =>
      toggleTariffDocVisibility(itemId, parentId),
    onSuccess: () => {
      if (databaseId) {
        queryClient.invalidateQueries({
          queryKey: ['tariffs', databaseId],
          exact: false,
        });
      } else {
        queryClient.invalidateQueries({ queryKey: tariffKeys.all() });
      }
    },
  });
};

// Mutation hook to delete tariff
export const useDeleteTariff = (databaseId?: string) => {
  const queryClient = useQueryClient();
  const { invalidateAllDatabaseKeys } = useInvalidateAllDatabaseKeys();

  return useMutation({
    mutationFn: (id: string) => deleteTariff(id),
    onSuccess: () => {
      if (databaseId) {
        queryClient.invalidateQueries({
          queryKey: ['tariffs', databaseId],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: [STATS_QUERY_KEYS.stats],
          exact: false,
        });
      } else {
        invalidateAllDatabaseKeys();
      }

      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogs(),
        exact: false,
      });
    },
  });
};

// Mutation hook to bulk delete tariffs
export const useDeleteBulkTariffs = (databaseId?: string) => {
  const queryClient = useQueryClient();
  const { invalidateAllDatabaseKeys } = useInvalidateAllDatabaseKeys();

  return useMutation({
    mutationFn: (tariffIds: string[]) => deleteBulkTariffs(tariffIds),
    onSuccess: () => {
      if (databaseId) {
        queryClient.invalidateQueries({
          queryKey: ['tariffs', databaseId],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: [STATS_QUERY_KEYS.stats],
          exact: false,
        });
      } else {
        invalidateAllDatabaseKeys();
      }

      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogs(),
        exact: false,
      });
    },
  });
};

// Mutation hook to create a single tariff
export const useCreateTariff = () => {
  const queryClient = useQueryClient();
  const { invalidateAllDatabaseKeys } = useInvalidateAllDatabaseKeys();

  return useMutation({
    mutationFn: ({
      tariffData,
      teamId,
    }: {
      tariffData: any;
      teamId?: string;
    }) => createTariff(tariffData, teamId),
    onSuccess: (data, variables) => {
      invalidateAllDatabaseKeys(variables.teamId);
      queryClient.invalidateQueries({
        queryKey: [STATS_QUERY_KEYS.stats],
        exact: false,
      });

      if (variables.tariffData?.database_id) {
        queryClient.invalidateQueries({
          queryKey: auditKeys.auditLogsByDatabase(
            variables.tariffData.database_id,
          ),
          exact: false,
        });
      }
    },
  });
};

// Mutation hook to bulk upload tariffs
export const useBulkUploadTariffs = () => {
  return useMutation({
    mutationFn: ({
      databaseId,
      file,
      createdBy,
      teamId,
      dateStrategy,
      spendMode,
    }: {
      databaseId: string;
      file: File;
      createdBy?: string;
      teamId?: string;
      dateStrategy?: 'current' | 'original';
      spendMode?: 'trial' | 'active';
    }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('database_id', databaseId);
      if (createdBy) {
        formData.append('created_by', createdBy);
      }
      if (dateStrategy) {
        formData.append('date_strategy', dateStrategy);
      }
      if (spendMode) {
        formData.append('spend_mode', spendMode);
      }
      return bulkUploadTariffs(formData, teamId);
    },
  });
};

// Mutation hook to create bulk tariffs without duplicate check
export const useCreateBulkTariffsWithoutDuplicateCheck = () => {
  return useMutation({
    mutationFn: ({
      tariffs,
      databaseId,
      createdBy,
      teamId,
      uploadStartTime,
    }: {
      tariffs: Partial<ITariff>[];
      databaseId: string;
      createdBy?: string;
      teamId?: string;
      uploadStartTime?: string;
    }) => {
      return createBulkTariffsWithoutDuplicateCheck(
        tariffs,
        databaseId,
        createdBy,
        teamId,
        uploadStartTime,
      );
    },
  });
};

export const useSaveTariffResult = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      saveTariffResult(id, data),
    onSuccess: (data, variables) => {
      // Invalidate the fetch query so the page refreshes with new data
      queryClient.invalidateQueries({ queryKey: ['tariff', variables.id] });
    },
  });
};
